import React, { useState, useRef } from "react";
import {
  Upload,
  X,
  Image as ImageIcon,
  Folder,
  Star,
  AlertCircle,
} from "lucide-react";
import { API_BASE_URL } from "../../config/api";

const ImageUpload = ({
  images,
  onChange,
  maxImages = 10,
  uploadFolder,
  onFolderChange,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [imageErrors, setImageErrors] = useState({}); // Track image loading errors
  const fileInputRef = useRef(null);

  // Get API base URL for image serving (from shared config for HTTPS in production)
  const apiBase = (API_BASE_URL || "").replace(/\/+$/, "");

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const newImages = fileArray.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      file,
      url: URL.createObjectURL(file),
      alt_text: file.name.replace(/\.[^/.]+$/, ""), // Remove file extension
      is_primary: images.length === 0 && index === 0,
      existing: false,
    }));

    const totalImages = images.length + newImages.length;
    if (totalImages > maxImages) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }

    onChange([...images, ...newImages]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (imageFiles.length > 0) {
      handleFileSelect(imageFiles);
    } else {
      alert("Please drop only image files (PNG, JPG, GIF, WebP)");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const removeImage = (imageId) => {
    const updatedImages = images
      .map((img) => {
        if (img.id === imageId) {
          if (img.existing) {
            return { ...img, remove: true };
          }
          return null;
        }
        return img;
      })
      .filter(Boolean);

    // If we removed the primary image, set the first remaining image as primary
    const visibleImages = updatedImages.filter((img) => !img.remove);
    if (
      visibleImages.length > 0 &&
      !visibleImages.some((img) => img.is_primary)
    ) {
      visibleImages[0].is_primary = true;
    }

    onChange(updatedImages);
  };

  const setPrimaryImage = (imageId) => {
    const updatedImages = images.map((img) => ({
      ...img,
      is_primary: img.id === imageId,
    }));
    onChange(updatedImages);
  };

  const updateAltText = (imageId, altText) => {
    const updatedImages = images.map((img) =>
      img.id === imageId ? { ...img, alt_text: altText } : img
    );
    onChange(updatedImages);
  };

  const updateCustomFilename = (imageId, customFilename) => {
    const updatedImages = images.map((img) =>
      img.id === imageId ? { ...img, custom_filename: customFilename } : img
    );
    onChange(updatedImages);
  };

  const handleImageError = (imageId) => {
    setImageErrors((prev) => ({ ...prev, [imageId]: true }));
  };

  const handleImageLoad = (imageId) => {
    setImageErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[imageId];
      return newErrors;
    });
  };

  // Format image URL to ensure it works properly
  const getImageUrl = (image) => {
    if (!image.existing) {
      return image.url; // Use blob URL for new images
    }
    if (image.url?.startsWith("http")) {
      return image.url; // Already a full URL
    }
    // Remove any leading '/uploads/' or 'uploads/'
    let cleanUrl = image.url
      ?.replace(/^\/uploads\//, "")
      .replace(/^uploads\//, "");
    return `${apiBase}/uploads/${cleanUrl}`;
  };

  const visibleImages = images.filter((img) => !img.remove);

  return (
    <div className="space-y-4">
      {/* Upload Folder Selection */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Folder size={20} className="text-gray-500" />
          <label className="text-sm font-medium text-gray-700">
            Upload Folder:
          </label>
        </div>
        <input
          type="text"
          value={uploadFolder || new Date().getFullYear().toString()}
          onChange={(e) => onFolderChange && onFolderChange(e.target.value)}
          placeholder="e.g., 2024, products, etc."
          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <span className="text-sm text-gray-500">
          Images will be stored in /uploads/{uploadFolder}/
        </span>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
          dragOver
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
        />

        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">
          <span className="font-medium">Click to upload</span> or drag and drop
        </p>
        <p className="text-xs text-gray-500">
          PNG, JPG, GIF, WebP up to 10MB each (max {maxImages} images)
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {visibleImages.length} / {maxImages} images uploaded
        </p>
      </div>

      {/* Image Preview Grid */}
      {visibleImages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {visibleImages.map((image) => (
            <div key={image.id} className="space-y-2">
              {/* Image Container with Controls */}
              <div className="relative group">
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                  {imageErrors[image.id] ? (
                    // Error state
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                      <AlertCircle size={24} />
                      <span className="text-xs mt-1">Image not found</span>
                    </div>
                  ) : (
                    <img
                      src={getImageUrl(image)}
                      alt={image.alt_text}
                      className="w-full h-full object-cover"
                      onError={() => handleImageError(image.id)}
                      onLoad={() => handleImageLoad(image.id)}
                    />
                  )}

                  {/* Primary Image Indicator */}
                  {image.is_primary && (
                    <div className="absolute top-2 left-2 bg-yellow-500 text-white p-1 rounded-full shadow-lg z-10">
                      <Star size={12} fill="currentColor" />
                    </div>
                  )}

                  {/* Action Buttons Overlay - Only covers the image area */}
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none group-hover:pointer-events-auto">
                    {!image.is_primary && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPrimaryImage(image.id);
                        }}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white p-2 rounded-full transition-colors"
                        title="Set as primary image"
                      >
                        <Star size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(image.id);
                      }}
                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                      title="Remove image"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Input Fields - Separated from image container */}
              <div className="space-y-1">
                {/* Alt Text Input */}
                <input
                  type="text"
                  value={image.alt_text || ""}
                  onChange={(e) => updateAltText(image.id, e.target.value)}
                  placeholder="Alt text..."
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                />

                {/* Custom File Name Input (only for new uploads) */}
                {!image.existing && (
                  <input
                    type="text"
                    value={image.custom_filename || ""}
                    onChange={(e) =>
                      updateCustomFilename(image.id, e.target.value)
                    }
                    placeholder="Custom filename (optional)..."
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  />
                )}

                {/* Image Info */}
                <div className="text-xs text-gray-500 flex items-center justify-between">
                  <span>
                    {image.existing ? (
                      <span className="text-green-600">Existing</span>
                    ) : (
                      <span className="text-blue-600">New upload</span>
                    )}
                    {image.is_primary && (
                      <span className="ml-2 text-yellow-600 font-medium">
                        Primary
                      </span>
                    )}
                  </span>
                  {image.file && (
                    <span className="text-gray-400">
                      {(image.file.size / 1024 / 1024).toFixed(1)}MB
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Progress/Status */}
      {visibleImages.some((img) => !img.existing) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-700">
            <ImageIcon size={16} className="inline mr-1" />
            {visibleImages.filter((img) => !img.existing).length} new image(s)
            ready for upload
          </p>
          <p className="text-xs text-blue-600 mt-1">
            Images will be uploaded when you save the product
          </p>
        </div>
      )}

      {/* Tips */}
      <div className="bg-gray-50 rounded-lg p-3">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Image Tips:</h4>
        <ul className="text-xs text-gray-600 space-y-1">
          <li>
            • Use high-quality images (recommended: 1200x1200px or larger)
          </li>
          <li>
            • The first image or marked primary image will be the main product
            image
          </li>
          <li>• Add descriptive alt text for better SEO and accessibility</li>
          <li>• Supported formats: JPG, PNG, GIF, WebP</li>
          <li>
            • Images will be automatically optimized and converted to WebP
            format
          </li>
          <li>• Maximum file size: 10MB per image</li>
        </ul>
      </div>
    </div>
  );
};

export default ImageUpload;
